<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-transcript-search-details">
    <div class="div-transcript-header">
      <div class="div-header-sentiment">
        <el-tooltip v-if="transcript" class="item" effect="dark" placement="bottom">
          <div v-if="transcript.sentiment == 'pos'" slot="content">Positive sentiment</div>
          <div v-else-if="transcript.sentiment == 'neg'" slot="content">Negative sentiment</div>
          <div v-else slot="content">Neutral sentiment</div>

          <div class="sentiment-icon-positive" v-if="transcript.sentiment == 'pos'"></div>
          <div class="sentiment-icon-neutral" v-else-if="transcript.sentiment == 'neu'"></div>
          <div class="sentiment-icon-negative" v-else-if="transcript.sentiment == 'neg'"></div>
        </el-tooltip>
        <div class="transcripts-viewer-date-header">{{ formatMessageHeaderDate(transcript.date) }}</div>
      </div>
      <div class="transcripts-viewer-time-header">{{ getTimeRange(transcript) }}</div>
      <div class="flex-container">
        <el-tooltip v-if="transcript.isSavedTranscript" ref="unSaveTooltip" class="save-item" content="unsave transcript" effect="dark" placement="bottom">
          <div><el-button v-if="transcript.isSavedTranscript" slot="reference" class="saved-transcript-golden-star" @click="handleRemoveSavedTranscript($event, transcript)"></el-button></div>
        </el-tooltip>
        <el-tooltip v-if="!transcript.isSavedTranscript" ref="saveTooltip" class="save-item" content="save transcript" effect="dark" placement="bottom">
          <div><el-button v-if="!transcript.isSavedTranscript" slot="reference" :class="{'showSummary':isTranscriptSummaryEnabled}" class="saved-transcript-grey-star" @click="handleSaveTranscript($event, transcript)"></el-button></div>
        </el-tooltip>
        <!-- Show summary -->
        <el-tooltip v-show="isTranscriptSummaryEnabled && !transcriptSummaryOpened" ref="summaryTooltip" :content="'View summary'" effect="dark" placement="bottom">
          <el-button class="summary-icon" @click="handleShowSummary(transcript)"></el-button>
        </el-tooltip>
        <el-tooltip v-show="isTranscriptSummaryEnabled && transcriptSummaryOpened" ref="summaryTooltip" :content="'Hide summary'" effect="dark" placement="bottom">
          <el-button class="summary-icon" @click="handleHideSummary(transcript)"></el-button>
        </el-tooltip>

        <wy-panel-header-transcript-info :transcript="transcript" :iconType="iconType" :isTranscriptSummaryEnabled="isTranscriptSummaryEnabled"/>

        <el-tooltip class="expend-item" effect="dark" placement="bottom">
          <div slot="content">Expand</div>
          <div class="transcript-expand-icon" @click="handleExpandClick"></div>
        </el-tooltip>
      </div>
    </div>

    <div class="div-transcript-content">
      <div>
        <div class="transcriptSummary" v-if="transcriptSummaryOpened && !transcriptSummaryNetworkError" v-loading="transcriptSummaryLoading">
          <div class="titleWrapper">
            <div class="summaryTitle"></div>
            Transcript Summary
          </div>
          <div v-if="transcript.contentSummary" class="summary" v-html="contentSummary"></div>
          <div class="footer">[ Generated by AI ]</div>
        </div>
        <div v-else-if="transcriptSummaryNetworkError" class="transcriptSummaryError">
          <div class="content">
            <div class="info">
              <el-button class="errorIcon" @click="handleHideSummary"></el-button>
              <div class="body">
                <div>Summary failed to load.</div>
                <el-button class="tryAgainBtn" @click="handleShowSummary(transcript)">Try again</el-button>
              </div>
            </div>
            <el-button class="closeIcon" @click="handleHideSummary(transcript)"></el-button>
          </div>
        </div>
        <div
          v-for="(message, index) in transcript.messages"
          :key="index"
          :class="{
            'div-transcript-row-bot': message.sentBy == 'Bot',
            'div-transcript-row-cus': message.sentBy == 'Customer',
            'div-transcript-row-agent': message.sentBy == 'Agent',
            'transcript-flex-col': (message.sentBy == 'Agent' && returnTRef(transcript.messages)[1] == index) || (message.sentBy == 'Bot' && returnTRef(transcript.messages)[0] == index),
          }"
          class="div-transcript-row"
        >
          <div v-if="message.sentBy == 'Bot' && returnTRef(transcript.messages)[0] == index" class="bot-connected-row">
            <span class="bot-connected-text">Virtual Agent Connected</span>
          </div>
          <div v-if="message.sentBy == 'Agent' && returnTRef(transcript.messages)[1] == index" class="agent-connected-row"><span class="agent-connected-text">Live Agent Connected</span></div>
          <div style="display: flex; flex-direction: column;">
            <div class="div-sent-by">
              {{ toTranscriptLabel(message.sentBy) }}, <span class="time-stamp-style">{{ handleTimeFormat(message.datetime) }}</span>
            </div>
            &nbsp;
            <div v-if="message && message.text.includes('||')" v-html="message.text.replace(/\|\|+/g, ' | ')" class="div-transcript-text-double-line"></div>
            <div v-else-if="message && message.text.includes('|')" v-html="message.text" class="div-transcript-text-double-line"></div>
            <div v-else v-html="message.text" class="div-transcript-text"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import WyPanelHeaderTranscriptInfo from '../panel-views/wy-panel-header-transcript-info.vue';
import WyUtils from '../../library/wy-utils.js';
import { mapActions, mapGetters, mapMutations } from 'vuex';

export default Vue.extend({
  name: 'wy-analytics-transcript-search-details',
  props: ['parent', 'transcript', 'transcriptClickEvent', 'transcriptSummaryOpened', 'transcriptSummaryNetworkError', 'from', 'iconType'],
  components: {
    'wy-panel-header-transcript-info': WyPanelHeaderTranscriptInfo,
  },
  data() {
    return {
      transcriptSummaryLoading: false,
    };
  },

  computed: {
    ...mapGetters(['companyConfig', 'transcriptInfoReference', 'isRoleAllow', 'isC1Auth']),
    isTranscriptSummaryEnabled() {
      if(this.isC1Auth) {
        return this.isRoleAllow(900007);
      }
      else {
        return (this.companyConfig ? this.companyConfig.enableTranscriptSummary : false);
      }
    },
    contentSummary() {
      return this.transcript.contentSummary;
    },
    pageName() {
      return this.from === 'Conversation Topics' ? 'Conversation Topics' : 'Transcript Search';
    },
    viewName() {
      return this.from === 'Conversation Topics' ? 'Conversation Topics Individual - Transcripts' : 'Transcript Search';
    },
  },
  methods: {
    ...mapActions(['fetchTranscriptSummary']),
    ...mapMutations(['updateTranscriptInfoReference']),

    triggerAmplitude(show = false, conversationId = 0, retrieved_from = '') {
      const eventName = show ? 'transcript_summary_show' : 'transcript_summary_hide';
      this.$router.logAmplitudeEvent({ eventName: eventName, filterProperties: {
        conversationId: conversationId,
        ...(show && { retrieved_from: retrieved_from }),
        transcript_is_expanded: false,
      }});
    },

    async handleShowSummary(transcript) {
      this.$emit('handle-transcript-summary', {
        conversationId: transcript.conversationId,
        opened: true,
      });
      this.transcriptSummaryLoading = true;

      const sessionData = sessionStorage.getItem(transcript.conversationId);
      if (sessionData || transcript.contentSummary) {
        this.transcriptSummaryLoading = false;

        const summary = sessionData ? JSON.parse(sessionData).summary : transcript.contentSummary;
        transcript.contentSummary =
          '<ul>' +
          summary
            .replace(/-/g, '')
            .split('\n')
            .map(function(item) {
              return '<li>' + item.trim() + '</li>';
            })
            .join('') +
          '</ul>';

        return this.triggerAmplitude(true, transcript.conversationId, 'cache - session storage');
      }

      const contentSummary = await this.fetchTranscriptSummary({
        conversationId: transcript.conversationId,
      });
      this.transcriptSummaryLoading = false;

      if (contentSummary.status == 200 && (contentSummary.data && contentSummary.data.summary)) {
        transcript.contentSummary =
          '<ul>' +
          contentSummary.data.summary
            .replace(/-/g, '')
            .split('\n')
            .map(function(item) {
              return '<li>' + item.trim() + '</li>';
            })
            .join('') +
          '</ul>';

        sessionStorage.setItem(transcript.conversationId, JSON.stringify(contentSummary.data));
        this.triggerAmplitude(true, transcript.conversationId, contentSummary.data.retrieved_from);
        return;
      }

      this.$emit('handle-transcript-summary', {
        conversationId: transcript.conversationId,
        opened: true,
        isError: true,
      });
    },

    handleHideSummary(transcript) {
      if (!this.transcriptSummaryNetworkError) {
        this.triggerAmplitude(false, transcript.conversationId);
      }

      this.$emit('handle-transcript-summary', {
        conversationId: transcript.conversationId,
        opened: false,
        isError: false,
      });
    },

    handleSaveTranscript(event, transcript) {
      this.parent.addSavedTranscript(event, transcript);
      if (this.$refs.saveTooltip && this.$refs.unSaveTooltip) {
        this.$refs.saveTooltip.$el.style.display = 'none';
        this.$refs.unSaveTooltip.$el.style.display = 'block';
      }
    },
    handleRemoveSavedTranscript(event, transcript) {
      this.parent.removeSavedTranscript(event, transcript);
      if (this.$refs.saveTooltip && this.$refs.unSaveTooltip) {
        this.$refs.unSaveTooltip.$el.style.display = 'none';
        this.$refs.saveTooltip.$el.style.display = 'block';
      }
    },
    handleTimeFormat(datetime) {
      return moment(datetime).format('h:mm a');
    },
    returnTRef(messages) {
      let sentByArr = [];
      for (let i = 0; i < messages.length; i++) {
        let entries = Object.entries(messages[i]);
        entries.map(([key, val]) => {
          if (key == 'sentBy') {
            sentByArr.push(val);
          }
        });
      }
      return [sentByArr.indexOf('Bot'), sentByArr.indexOf('Agent')];
    },
    formatMessageHeaderDate(date) {
      return WyUtils.formatDateWithDay(date);
    },

    getTimeRange(transcript) {
      return WyUtils.getTimeRange(transcript);
    },

    toTranscriptLabel(val) {
      return WyUtils.toTranscriptLabel(val);
    },

    handleTimeInNiceFormat(Millis) {
      let seconds = Millis / 1000;

      return WyUtils.toNiceTimeFormat(seconds);
    },
    handleExpandClick(e) {
      e.stopPropagation();
      if (this.parent.controller && this.parent.controller.$refs && this.parent.controller.$refs.transcriptSearchFilters) {
        if (this.parent.controller.$refs.transcriptSearchFilters.$refs.allFilters) {
          this.parent.controller.$refs.transcriptSearchFilters.$refs.allFilters.showPopper = false;
        }
        if (this.parent.controller.$refs.transcriptSearchFilters.$refs.localAgentFilter) {
          this.parent.controller.$refs.transcriptSearchFilters.$refs.localAgentFilter.showPopper = false;
        }
      }
      this.transcriptClickEvent(this.transcript);

      // For metric analysis popover
      if (this.transcriptInfoReference) {
        this.transcriptInfoReference.visible = false;
        this.updateTranscriptInfoReference(null);
      }
    },
  },
});
</script>

<style lang="scss">
.wy-analytics-transcript-search-details {
  ul {
    margin-top: -4px;
  }

  ul,
  li {
    list-style-type: disc !important;
    list-style-position: inside !important;
    padding: 4px 0px 0px 0px !important;
    text-indent: -1.5em !important;
    margin-left: 1.5em !important;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
    font-size: 14.22px !important;
  }

  .div-transcript-header {
    border-bottom: 1px #e8e8e8 solid;
    // max-height: 32px;
    height: 35px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    background-color: #ffffff;
    border-top-right-radius: 10px;
    border-top-left-radius: 10px;
    align-items: center;

    .div-header-sentiment {
      display: flex;
      gap: 14px;
      align-items: center;
    }

    .expand-icon-hide {
      display: none;
    }

    .transcripts-viewer-date-header {
      font-size: 14.22px;
      font-weight: 400;
      min-width: 98px;
      // margin-left: -60px;
      white-space: nowrap;
    }

    .transcripts-viewer-time-header {
      float: right;
      font-size: 14.22px;
      font-weight: 400;
      padding-left: 18px;
      white-space: nowrap;
    }

    // @media (max-width: 1679px) {
    //   .transcripts-viewer-time-header {
    //     display: none;
    //   }
    // }

    .el-tooltip.save-item {
      position: relative;
      left: 24px;
      top: 0px;
    }
    .el-tooltip.expend-item {
      position: relative;
      top: -10px;
    }

    .el-tooltip.info-item {
      line-height: 0;
    }

    .saved-transcript-golden-star {
      background-image: url('../../assets/icons/icon_golden_star.svg');
      background-size: 100%;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      height: 20px;
      width: 20px;
      border: 0px;
      margin: 0px !important;
      padding: 0px !important;
      position: relative;
      // top: 6px;
      border-radius: 0px;
    }

    .saved-transcript-grey-star {
      float: right;
      background-image: url('../../assets/icons/icon_grey_star.svg');
      background-size: 100%;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      height: 20px;
      width: 20px;
      border: 0px;
      margin: 0px !important;
      padding: 0px !important;
      position: relative;
      // top: 6px;
      border-radius: 0px;
    }

    .transcript-expand-icon {
      background-image: url('../../assets/icons/icon_transcript_search_expand.svg');
      cursor: pointer;
      background-size: 100%;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      height: 20px;
      width: 20px;
      border: 0px;
      margin: 0px !important;
      padding: 0px !important;
      position: relative;
      top: 1px !important;
      left: 0px;
      border-radius: 0px;
    }

    .el-button:focus {
      background-color: unset;
    }

    .el-button:hover {
      background-color: unset;
    }

    .flex-container {
      display: flex;
      justify-content: space-around;
      width: 150px;
      line-height: 0;

      .transcripts-viewer-header-right {
        line-height: 18px;

        .popper-item {
          margin-bottom: 15px;
        }

        overflow-y: auto;
        height: 204px !important;
        padding-right: 20px;

        &::-webkit-scrollbar {
          background-color: #000 !important;
          width: 8px !important;
        }

        &::-webkit-scrollbar-thumb {
          background: #c8c8c8 !important;
          border-radius: 5px;
        }
      }
    }

    .sentiment-icon-positive {
      cursor: default;
      background-image: url('../../assets/icons/icon_positive_senti.svg');
      background-size: 80%;
      background-repeat: no-repeat;
      background-position-y: 1px;
      background-position-x: 5px;
      position: relative;
      left: 7px;
      top: 0px;
      border-radius: 100px;
      border: 0px;
      margin: 6px 13px 0 0;
      padding: 12px 10px 14px 14px;
      float: left;
    }

    .sentiment-icon-neutral {
      cursor: default;
      background-image: url('../../assets/icons/icon_neutral_senti.svg');
      background-size: 80%;
      background-repeat: no-repeat;
      background-position-y: 1px;
      background-position-x: 5px;
      position: relative;
      top: 0px;
      left: 7px;
      border-radius: 100px;
      border: 0px;
      margin: 6px 0px 0 0;
      padding: 12px 10px 14px 14px;
      float: left;
    }

    .sentiment-icon-negative {
      cursor: default;
      background-image: url('../../assets/icons/icon_negative_senti.svg');
      background-size: 80%;
      background-repeat: no-repeat;
      background-position-y: 1px;
      background-position-x: 5px;
      position: relative;
      top: 0px;
      left: 7px;
      border-radius: 100px;
      border: 0px;
      margin: 6px 0px 0 0;
      padding: 12px 10px 14px 14px;
      float: left;
    }
  }

  .summary-icon {
    background-image: url('../../assets/icons/summary_icon.svg');
    background-size: 100%;
    background-repeat: no-repeat;
    background-position-y: 0px;
    background-position-x: 0px;
    position: relative;
    height: 20px;
    width: 20px;
    border: 0px;
    position: relative;
    top: 1px;
    left: 10px;
    margin: 0px !important;
    padding: 0px !important;
    border-radius: 0px;
  }

  .transcriptSummaryError {
    display: flex;
    align-items: center;
    font-size: 14px;
    margin: 6px 10px;
    padding: 6px 40px 30px 40px;
    border-bottom: 1px solid #e5e5e5;
    min-height: 150px;

    .content {
      display: flex;
      width: 100%;
      justify-content: space-between;
      padding: 16px;
      border: 1px solid #ebafba;

      .info {
        display: flex;
        gap: 24px;
        font-weight: 500;
        font-size: 14.22px;

        .body {
          display: flex;
          flex-direction: column;
          font-weight: 600;
          gap: 16px;
        }

        .tryAgainBtn {
          border-radius: 4px;
          max-width: 100px;
          max-height: 35px;
          font-size: 16px;
          color: #202223;
          font-weight: 400;
          border: 1px solid #ebafba;

          display: flex;
          align-items: center;
          justify-content: center;

          width: 100%;
          height: 100%;

          &:hover {
            background-color: #f4f6f8;
          }
        }
      }

      .errorIcon {
        background-image: url('../../assets/icons/icon_error_square.svg');
        height: 12px;
        width: 12px;
        background-repeat: no-repeat;
        background-position-y: 0px;
        background-position-x: 3px;
        margin-top: -2px;
        margin-bottom: 8px;
        padding: 12px;
        border: none;
        cursor: default;

        &:hover {
          background-color: unset;
        }
      }

      .closeIcon {
        background-image: url('../../assets/icons/icon_customer_close.svg');
        height: 12px;
        width: 16px;
        background-repeat: no-repeat;
        background-position-y: 6px;
        background-position-x: 6px;
        margin-top: -2px;
        margin-right: 2px;
        margin-bottom: 8px;
        padding: 12px;
        padding-right: 12px;
        border: none;
        cursor: pointer;

        &:hover {
          background-color: transparent;
        }
      }
    }
  }

  .transcriptSummary {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    font-size: 14px;
    margin: 6px 10px;
    padding: 6px 24px 0px 24px;
    border-bottom: 1px solid #e5e5e5;
    min-height: 150px;

    .titleWrapper {
      font-weight: bold;
      font-size: 14px;
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;

      .summaryTitle {
        background-image: url('../../assets/icons/summary_title_icon.svg');
        background-size: 80%;
        background-repeat: no-repeat;
        background-position-y: 3px;
        background-position-x: 5px;
        position: relative;
        left: 7px;
        border-radius: 100px;
        border: 0px;
        top: 0px;
        margin: -3px 16px 0 0;
        padding: 12px 10px 18px 14px;
        float: left;
        color: var(--site-font-tertiary-color);
      }
    }

    .summary {
      color: rgb(75, 75, 75);
      margin-top: 14px;
    }

    .footer {
      margin: 20px 0px;
      margin-right: 18px;
      display: flex;
      justify-content: flex-end;
      width: 100%;
      color: #8c8c8c;
      font-size: 12px;
    }
  }

  .div-transcript-content {
    // height: 328px;
    height: calc(100% - 44px);
    // min-width: 257px;
    width: 100%;
    padding: 5px 0px;
    overflow-y: auto;
    overflow-x: hidden;
    font-size: 12px;

    &::-webkit-scrollbar {
      width: 20px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 160px;
      border: 2px solid transparent;
      border-top: 4px solid transparent;
      background-clip: content-box;
      background-color: #cad2da;
    }

    &::-webkit-scrollbar-track {
      background: #fff;
      border-top: 1px solid #cad2da;
    }

    &::-webkit-scrollbar:vertical {
      width: 14px;
    }

    &::-webkit-scrollbar-thumb:vertical {
      border-radius: 160px;
      border: 2px solid transparent;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      background-clip: content-box;
      background-color: #cad2da;
    }

    &::-webkit-scrollbar-track:vertical {
      border-left: 1px solid #eee;
      border-top: none !important;
    }

    .bot-connected-row {
      padding: 10px 15px 8px 11px;
      border-bottom: 10px #fff solid;
      cursor: default;
      width: calc(100% - 6px);
      background-image: url('../../assets/icons/icon_transcript_bot.svg');
      background-repeat: no-repeat;
      position: relative;
      right: 11px;
      margin-bottom: 10px;
      background-position-y: 9px;
      background-position-x: 33%;
      // need to refactor the positioning with flexbox...!
    }

    .agent-connected-row {
      padding: 10px 15px 8px 11px;
      border-bottom: 10px #fff solid;
      cursor: default;
      width: calc(100% - 6px);
      background-image: url('../../assets/icons/icon_transcript_agent.svg');
      background-repeat: no-repeat;
      position: relative;
      right: 11px;
      margin-bottom: 10px;
      background-position-y: 9px;
      background-position-x: 29%;
    }

    .div-transcript-row {
      display: inline-flex;
      padding: 10px 15px 8px 11px;
      min-width: 245px;
      background-color: #effcff;
      margin: 5px 10px;
      width: calc(100% - 46px);
      cursor: default;

      .div-sent-by {
        font-weight: 500;
        font-size: 14.22px;
        padding-right: 10px;
        min-width: 33px;
        line-height: 14px;
        color: #000;

        span.time-stamp-style {
          color: #4b5158;
          font-size: 14.22px;
          font-weight: 400;
          font-weight: 400;
        }
      }

      .div-transcript-text-double-line {
        word-break: break-word;
        // min-width: 178px;
        padding-left: 23px;
        padding-right: 11px;
        color: #106fbc;
        background-image: url('../../assets/icons/icon_transcript_doubleline.svg');
        background-repeat: no-repeat;
        background-size: 14px;
        font-size: 14px;
        background-position-y: -1px;
        background-position-x: 0%;

        .highlight-keywords {
          background-color: rgba(255, 177, 0, 0.22);
        }
      }

      .div-transcript-text {
        word-break: break-word;
        font-size: 14.22px;
        width: 100%;
        color: var(--site-font-secondary-color);

        .highlight-keywords {
          background-color: rgba(255, 177, 0, 0.22);
        }
      }
    }

    .div-transcript-row-bot {
      background-color: #effcff;
      width: calc(100% - 46px) !important;
    }

    .div-transcript-row.div-transcript-row-cus {
      background-color: #f8f6f6 !important;
    }

    .div-transcript-row.div-transcript-row-bot.transcript-flex-col,
    .div-transcript-row.div-transcript-row-agent.transcript-flex-col {
      padding: 0px 9px 10px 11px;
      display: flex;
      width: calc(100% - 38px) !important;
      flex-direction: column;
    }

    span.agent-connected-text {
      margin-left: 34%;
      margin-bottom: 15%;
      font-size: 14.22px;
      font-weight: 700;
      white-space: nowrap;
    }

    span.bot-connected-text {
      margin-left: 38%;
      margin-bottom: 15%;
      font-size: 14.22px;
      font-weight: 700;
      // don't break line
      white-space: nowrap;
    }

    .div-transcript-row-agent {
      background-color: #eeeefa;
      width: calc(100% - 46px) !important;
    }
  }
}

.contact-reason-transcript-tab-info-pop-over {
  background: black;
  color: #e4e8ec;
  font-size: 14px;
  padding: 12px 15px 10px 15px;
  border: black !important;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;

  .popper__arrow {
    border-top-color: var(--site-font-chart-color) !important;
    &:after {
      border-top-color: var(--site-font-chart-color) !important;
    }
  }
}
</style>
